import type { CrmRecordPermission } from '@/services/bff/types/record-permission';

type PermissionCarrier = {
  recordPermission?: CrmRecordPermission | null;
};

const normalizePermission = (
  value?: CrmRecordPermission | null,
): CrmRecordPermission => {
  if (!value) {
    return {};
  }
  return value;
};

export const resolveRecordPermission = (
  record?: PermissionCarrier | null,
): CrmRecordPermission => {
  const nested = normalizePermission(record?.recordPermission);
  const rawSummaryFields = nested.fieldVisibility?.summaryFields;
  const summaryFields = Array.isArray(rawSummaryFields)
    ? rawSummaryFields
        .map((field: string) => String(field || '').trim())
        .filter(Boolean)
    : undefined;
  return {
    ...nested,
    canDiscover: nested?.canDiscover ?? nested?.canRead,
    canRead: nested?.canRead ?? nested?.canViewDetail,
    canViewDetail: nested?.canViewDetail ?? nested?.canRead,
    fieldVisibility:
      summaryFields || nested?.fieldVisibility
        ? {
            ...nested?.fieldVisibility,
            summaryFields,
          }
        : nested?.fieldVisibility,
  };
};

export const canReadRecord = (record?: PermissionCarrier | null) => {
  const permission = resolveRecordPermission(record);
  return Boolean(permission.canRead ?? permission.canViewDetail);
};

export const canDiscoverRecord = (record?: PermissionCarrier | null) => {
  const permission = resolveRecordPermission(record);
  if (
    permission.canDiscover === undefined &&
    permission.canRead === undefined &&
    permission.canViewDetail === undefined
  ) {
    return true;
  }
  return Boolean(
    permission.canDiscover ?? permission.canRead ?? permission.canViewDetail,
  );
};

export const isDiscoverableUnreadableRecord = (
  record?: PermissionCarrier | null,
) => canDiscoverRecord(record) && !canReadRecord(record);

export const getRecordSummaryFields = (record?: PermissionCarrier | null) => {
  const permission = resolveRecordPermission(record);
  return permission.fieldVisibility?.summaryFields || [];
};

export const canViewRecordSummaryField = (
  record: PermissionCarrier | null | undefined,
  field: string,
) => {
  const normalizedField = String(field || '').trim();
  if (!normalizedField) {
    return false;
  }
  return getRecordSummaryFields(record).includes(normalizedField);
};

export const canActOnRecord = (
  record: PermissionCarrier | null | undefined,
  actionCode: string,
) => {
  if (!actionCode) {
    return false;
  }
  const permission = resolveRecordPermission(record);
  const actionMap = permission.actions;
  if (actionMap && actionCode in actionMap) {
    return Boolean(actionMap[actionCode]);
  }
  if (
    permission.canDiscover === undefined &&
    permission.canRead === undefined &&
    permission.canViewDetail === undefined
  ) {
    return true;
  }
  return canReadRecord(record);
};
